import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="stripe-elements"
export default class extends Controller {
  static targets = ["submitButton", "cardToken", "paymentMethodId", "form"]
  static values = {
    publishableKey: { type: String, default: "" },
    clientSecret: String,
    reservationId: String,
    paymentId: String
  }
  
  connect() {
    console.log("stripe-elements #connect")
    // Initialize Stripe Elements when the controller connects
    this.initializeStripeElements()
  }
  
  disconnect() {
    // Clean up when the controller disconnects
    if (this.cardElement) {
      this.cardElement.destroy()
    }
  }
  
  initializeStripeElements() {
    if (typeof Stripe === 'undefined') {
      console.error('Stripe is not loaded')
      this.displayError('Stripe.js failed to load. Please refresh the page and try again.')
      return
    }
    
    // Get the publishable key directly from the data attribute or use the default from the environment
    let publishableKey = this.publishableKeyValue || document.querySelector('meta[name="stripe-publishable-key"]')?.content
    
    // Log the key for debugging (without revealing the actual key)
    console.log("Stripe publishable key available:", !!publishableKey)
    
    if (!publishableKey) {
      console.error('Stripe publishable key not found')
      this.displayError('Stripe configuration error. Please contact support.')
      return
    }
    
    try {
      // Initialize Stripe - the key must be a valid publishable key (pk_test_*)
      this.stripe = Stripe(publishableKey)
      
      // Create elements instance
      const elements = this.stripe.elements()
      
      // Create card element with improved styling
      this.cardElement = elements.create('card', {
        style: {
          base: {
            color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#374151',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
              color: document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'
            }
          },
          invalid: {
            color: '#ef4444',
            iconColor: '#ef4444'
          }
        }
      })
      
      // Mount the card element to the DOM
      this.cardElement.mount('#card-element')
      
      // Handle real-time validation errors from the card element
      this.cardElement.on('change', (event) => {
        const displayError = document.getElementById('card-errors')
        if (event.error) {
          displayError.textContent = event.error.message
        } else {
          displayError.textContent = ''
        }
      })
      
      console.log("Stripe Elements initialized successfully")
    } catch (error) {
      console.error('Error initializing Stripe Elements:', error)
      this.displayError('Failed to initialize payment form. Please refresh the page and try again.')
    }
  }
  
  // Handle form submission for in-person payments
  async handleSubmit(event) {
    event.preventDefault()
    
    if (!this.stripe || !this.cardElement) {
      console.error('Stripe or card element not initialized')
      this.displayError('Payment system not initialized. Please refresh the page and try again.')
      return
    }
    
    // Disable the submit button to prevent multiple submissions
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.innerHTML = '<i class="ph ph-spinner ph-spin mr-2"></i> Processing...'
    
    try {
      // Get the cardholder name from the form
      const cardholderName = document.getElementById('cardholder-name').value
      
      if (!cardholderName) {
        throw new Error('Cardholder name is required')
      }
      
      console.log("Creating payment method...")
      
      // Create a payment method using the card element
      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardElement,
        billing_details: {
          name: cardholderName
        }
      })
      
      if (error) {
        // Display error to the customer
        console.error('Payment method creation error:', error)
        this.displayError(error.message)
        
        // Re-enable the submit button
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.innerHTML = '<i class="ph ph-credit-card mr-2"></i> Process Payment'
      } else {
        console.log("Payment method created successfully:", paymentMethod.id)
        
        // Set the payment method ID in the hidden input field
        this.paymentMethodIdTarget.value = paymentMethod.id
        
        // Submit the form using Rails UJS to handle CSRF token
        // Rule applied: Protect against common web vulnerabilities (XSS, CSRF, SQL injection)
        const csrfToken = document.querySelector('meta[name="csrf-token"]').content
        const formData = new FormData(this.formTarget)
        
        fetch(this.formTarget.action, {
          method: this.formTarget.method,
          headers: {
            'X-CSRF-Token': csrfToken,
            'Accept': 'text/html,application/xhtml+xml,application/xml'
          },
          body: formData,
          credentials: 'same-origin'
        })
        .then(response => {
          if (response.redirected) {
            window.location.href = response.url
          } else {
            return response.text().then(html => {
              const parser = new DOMParser()
              const doc = parser.parseFromString(html, 'text/html')
              const newBody = doc.body
              document.body.innerHTML = newBody.innerHTML
              Turbo.visit(window.location.href, { action: 'replace' })
            })
          }
        })
        .catch(error => {
          console.error('Form submission error:', error)
          this.displayError('An error occurred while processing your payment. Please try again.')
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.innerHTML = '<i class="ph ph-credit-card mr-2"></i> Process Payment'
        })
      }
    } catch (error) {
      console.error('Error processing payment:', error)
      
      // Display error to the customer
      this.displayError(error.message || 'An unexpected error occurred. Please try again.')
      
      // Re-enable the submit button
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.innerHTML = '<i class="ph ph-credit-card mr-2"></i> Process Payment'
    }
  }
  
  setupFormSubmission() {
    if (!this.hasSubmitButtonTarget) return
    
    this.submitButtonTarget.addEventListener('click', async (event) => {
      event.preventDefault()
      
      // Show processing state
      const processingElement = document.getElementById('payment-processing')
      if (processingElement) processingElement.classList.remove('hidden')
      
      await this.handleSubmit(event)
    })
  }
  
  // Helper method to display errors
  displayError(message) {
    const errorElement = document.getElementById('card-errors')
    if (errorElement) {
      errorElement.textContent = message
      errorElement.classList.remove('hidden')
    } else {
      console.error('Error element not found, message was:', message)
    }
  }
}
